import {
  getPersonnel as getPersonnelSql,
  getScreenPermissionsByType,
  setScreenPermission,
  getWmsPermissions,
  setWmsPermissions,
} from '../../../services/migration/personnelDataService';

export type WmsScreenType = 'picking' | 'packing' | 'receiving' | 'cyclecount';

export interface WmsPersonnelPermissions {
  canShortPick: boolean;
  canCancelWave: boolean;
  canReassignWave: boolean;
  canSelfAssignOrder: boolean;
  canCompleteWave: boolean;
  canSorting: boolean;
}

export interface ResolvedPersonnel {
  personnelId: string;
  personnelName: string;
}

const DEFAULT_PERMISSIONS: WmsPersonnelPermissions = {
  canShortPick: false,
  canCancelWave: false,
  canReassignWave: false,
  canSelfAssignOrder: false,
  canCompleteWave: true,
  canSorting: true,
};

const clean = (value?: string | null) => (value || '').trim().toLowerCase();

/**
 * WMS personel/yetki erisimi SADECE SQL (servis = hook'suz, dogrudan personnelDataService).
 * - Ekran ERISIMI: SystemUserScreenPermissions screenType='wmsScreens' (screenId + isEnabled).
 * - Ekran AKSIYON izinleri: /api/hr/wms-screens/{screenId}/personnel-permissions (isEnabled + settings).
 */
export const wmsPersonnelPermissionService = {
  async resolvePersonnel(companyId: string, email: string): Promise<ResolvedPersonnel | null> {
    const expected = clean(email);
    try {
      const personnel = await getPersonnelSql(companyId);
      const match = (personnel || []).find((p: any) => clean(p.email) === expected);
      if (match) {
        return { personnelId: match.id, personnelName: match.name || 'Isimsiz Personel' };
      }
    } catch (e) {
      console.warn('[wmsPersonnelPermissionService] resolvePersonnel SQL hatasi:', e);
    }
    return null;
  },

  async hasScreenAccess(_companyId: string, personnelId: string, screenId: string): Promise<boolean> {
    try {
      const rows = await getScreenPermissionsByType(personnelId, 'wmsScreens');
      const row = rows.find((r) => String(r.screenId) === String(screenId));
      if (row) return row.isEnabled === true;
    } catch (e) {
      console.warn('[wmsPersonnelPermissionService] hasScreenAccess SQL hatasi:', e);
    }
    return false;
  },

  async getMyWmsScreens(_companyId: string, personnelId: string): Promise<string[]> {
    try {
      const rows = await getScreenPermissionsByType(personnelId, 'wmsScreens');
      const ids = rows.filter((r) => r.isEnabled && r.screenId != null).map((r) => String(r.screenId));
      if (ids.length > 0) return ids;
    } catch (e) {
      console.warn('[wmsPersonnelPermissionService] getMyWmsScreens SQL hatasi:', e);
    }
    return [];
  },

  async getScreenActionPermissions(
    _companyId: string,
    screenId: string,
    personnelId: string
  ): Promise<WmsPersonnelPermissions> {
    let raw: Partial<WmsPersonnelPermissions> | null = null;
    try {
      const result = await getWmsPermissions(screenId, personnelId);
      if (result.found && result.settings) raw = result.settings as Partial<WmsPersonnelPermissions>;
    } catch (e) {
      console.warn('[wmsPersonnelPermissionService] getScreenActionPermissions SQL hatasi:', e);
    }
    if (!raw) return DEFAULT_PERMISSIONS;
    return {
      canShortPick: raw.canShortPick ?? DEFAULT_PERMISSIONS.canShortPick,
      canCancelWave: raw.canCancelWave ?? DEFAULT_PERMISSIONS.canCancelWave,
      canReassignWave: raw.canReassignWave ?? DEFAULT_PERMISSIONS.canReassignWave,
      canSelfAssignOrder: raw.canSelfAssignOrder ?? DEFAULT_PERMISSIONS.canSelfAssignOrder,
      canCompleteWave: raw.canCompleteWave ?? DEFAULT_PERMISSIONS.canCompleteWave,
      canSorting: raw.canSorting ?? DEFAULT_PERMISSIONS.canSorting,
    };
  },

  async setScreenAccess(
    _companyId: string,
    personnelId: string,
    screenId: string,
    value: boolean
  ): Promise<void> {
    await setScreenPermission(personnelId, 'wmsScreens', String(screenId), value);
  },

  async setScreenActionPermissions(
    _companyId: string,
    screenId: string,
    personnelId: string,
    permissions: WmsPersonnelPermissions
  ): Promise<void> {
    await setWmsPermissions(String(screenId), personnelId, true, permissions);
  },
};

export const wmsDefaultPersonnelPermissions = DEFAULT_PERMISSIONS;
